import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Typography, Slider, MenuItem, InputAdornment, Box } from '@mui/material';
import { Subcontractor } from '../../services/subcontractorService';
import { Project } from '../../services/projectService';
import protocolService from '../../services/protocolService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../layouts/Layout';

export interface PocrFormValues {
  protocolDate: string;
  workFromDate: string;
  workToDate: string;
  contractValue: number;
  progressPercent: number;
  valueSinceStart: number; // wyliczane
  valueInPeriod: number;   // wyliczane
  remainingToInvoice: number; // wyliczane
  generalResponsible1: string;
  generalResponsible2?: string;
  subcontractorPerson1: string;
  subcontractorPerson2?: string;
  defectsNotes?: string;
  summaryDecision: 'bez_uwag' | 'z_uwagami' | 'nie_odbiera_do_usuniecia' | 'nie_odbiera_nie_do_usuniecia';
}

interface GeneratePocrDialogProps {
  open: boolean;
  onClose: () => void;
  subcontractor: Subcontractor;
  project?: Project | null;
  onSubmit: (values: PocrFormValues) => void;
}

const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

const GeneratePocrDialog: React.FC<GeneratePocrDialogProps> = ({ open, onClose, subcontractor, project, onSubmit }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const today = formatDate(new Date());

  const getResponsibleName = React.useCallback((proj: any, index: 1 | 2): string => {
    const fn = proj?.[index === 1 ? 'resp1_first_name' : 'resp2_first_name'] || proj?.[`responsiblePerson${index}`]?.first_name;
    const ln = proj?.[index === 1 ? 'resp1_last_name' : 'resp2_last_name'] || proj?.[`responsiblePerson${index}`]?.last_name;
    const dn = proj?.[index === 1 ? 'resp1_display_name' : 'resp2_display_name'] || proj?.[`responsiblePerson${index}`]?.display_name;
    const email = proj?.[index === 1 ? 'resp1_email' : 'resp2_email'] || proj?.[`responsiblePerson${index}`]?.email;
    if (fn || ln) return `${fn || ''} ${ln || ''}`.trim();
    if (dn) return dn;
    if (email) return email;
    return proj?.[index === 1 ? 'responsible_person1' : 'responsible_person2'] || '';
  }, []);

  const [protocolDate, setProtocolDate] = React.useState(today);
  const [workFromDate, setWorkFromDate] = React.useState('');
  const [workToDate, setWorkToDate] = React.useState(today);
  const initialProgress = typeof (subcontractor as any).progress === 'number' ? (subcontractor as any).progress as number : Number((subcontractor as any).progress || 0);
  const [progressPercent, setProgressPercent] = React.useState<number>(initialProgress);
  const [generalResponsible1, setGeneralResponsible1] = React.useState<string>(getResponsibleName(project, 1));
  const [generalResponsible2, setGeneralResponsible2] = React.useState<string>(getResponsibleName(project, 2));
  const [subPerson1, setSubPerson1] = React.useState<string>('');
  const [subPerson2, setSubPerson2] = React.useState<string>('');
  const [defects, setDefects] = React.useState<string>('');
  const [summary, setSummary] = React.useState<'bez_uwag' | 'z_uwagami' | 'nie_odbiera_do_usuniecia' | 'nie_odbiera_nie_do_usuniecia'>('bez_uwag');
  const [valueInPeriod, setValueInPeriod] = React.useState<number>(0);
  const [minProgress, setMinProgress] = React.useState<number>(0);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!open) return;
    // Reset pól
    setProtocolDate(today);
    setWorkToDate(today);
    setGeneralResponsible1(getResponsibleName(project, 1));
    setGeneralResponsible2(getResponsibleName(project, 2));
    setSubPerson1('');
    setSubPerson2('');
    setDefects('');
    setSummary('bez_uwag');

    const cv = Number(subcontractor.contract_value || 0);

    // Pobierz poprzednie protokoły POCR dla projektu i danego podwykonawcy
    (async () => {
      try {
        const projectId = project?.project_id || (project as any)?.id;
        let lastProgress = initialProgress;
        let fromDate: string = subcontractor.signed_date || '';
        setMinProgress(0);

        if (projectId) {
          const list: any = await protocolService.getByProject(projectId);
          const items: any[] = Array.isArray(list) ? list : (list?.data || []);
          const pocrForSub = items
            .filter((it: any) => it.type === 'POCR' && String(it.subcontractor_id) === String((subcontractor as any).id))
            .sort((a: any, b: any) => (Number(b.sequence_number || 0) - Number(a.sequence_number || 0)) || new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          if (pocrForSub.length > 0) {
            const last = pocrForSub[0];
            // work_from_date = poprzedni work_to_date + 1 dzień
            if (last.work_to_date) {
              const d = new Date(last.work_to_date);
              d.setDate(d.getDate() + 1);
              fromDate = formatDate(d);
            }
            // progress domyślnie = poprzedni
            lastProgress = Number(last.progress_percent || 0);
            setMinProgress(lastProgress);
            // Skumulowana wartość od początku z poprzedniego protokołu
            setPrevSinceStart(Number(last.value_since_start || 0));
          } else {
            // Pierwszy protokół – od daty podpisania umowy
            fromDate = subcontractor.signed_date || '';
            setMinProgress(0);
            setPrevSinceStart(0);
          }
        }

        // Ustawienia domyślne
        setWorkFromDate(fromDate || '');
        setProgressPercent(lastProgress);
        // Domyślnie w nowym formularzu wartość w okresie = 0
        setValueInPeriod(0);
      } catch (e) {
        // W razie błędu fallback do poprzedniej logiki
        setWorkFromDate('');
        setProgressPercent(initialProgress);
        setPrevSinceStart(0);
        setValueInPeriod(0);
      }
    })();
  }, [open, project, subcontractor, today, initialProgress, getResponsibleName]);

  const contractValue = Number(subcontractor.contract_value || 0);
  // Poprzednia skumulowana "wartość prac od początku" z ostatniego POCR
  const [prevSinceStart, setPrevSinceStart] = React.useState<number>(0);

  const valueSinceStart = React.useMemo(() => {
    return round2(Math.max(0, prevSinceStart + valueInPeriod));
  }, [prevSinceStart, valueInPeriod]);

  const remainingToInvoice = React.useMemo(() => {
    const remaining = contractValue - valueSinceStart;
    return remaining < 0 ? 0 : remaining;
  }, [contractValue, valueSinceStart]);

  const formatPLN = (val: number) => new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(val);

  // Synchronizacja – zmiana suwaka lub pola % wpływa na wartość w okresie
  const syncValueInPeriodFromProgress = (percent: number) => {
    const target = Math.max(0, contractValue * (percent / 100));
    setValueInPeriod(round2(Math.max(0, target - prevSinceStart)));
  };

  const handleProgressSlider = (_: Event, v: number | number[]) => {
    const pRaw = Array.isArray(v) ? v[0] : v;
    const p = Math.max(minProgress, pRaw);
    setProgressPercent(p);
    syncValueInPeriodFromProgress(p);
  };

  const handleProgressInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.target.value);
    const p = isNaN(raw) ? 0 : Math.max(minProgress, Math.min(100, raw));
    setProgressPercent(p);
    syncValueInPeriodFromProgress(p);
  };

  const handleValueInPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.target.value.replace(',', '.'));
    const val = isNaN(raw) ? 0 : Math.max(0, raw);
    const v2 = round2(val);
    setValueInPeriod(v2);
    // Przelicz procent zaawansowania na podstawie nowej wartości w okresie
    const sinceStart = prevSinceStart + v2;
    const p = contractValue > 0 ? Math.max(0, Math.min(100, (sinceStart / contractValue) * 100)) : 0;
    setProgressPercent(Math.max(minProgress, Number(p.toFixed(2))));
  };

  const handleSubmit = async () => {
    const payload = {
      protocolDate,
      workFromDate,
      workToDate,
      contractValue,
      progressPercent,
      valueSinceStart,
      valueInPeriod,
      remainingToInvoice,
      generalResponsible1,
      generalResponsible2,
      subcontractorPerson1: subPerson1,
      subcontractorPerson2: subPerson2,
      defectsNotes: defects,
      summaryDecision: summary,
    } as const;

    try {
      setSubmitting(true);
      showNotification('Trwa generowanie protokołu...', 'info');
      await protocolService.createProtocol({
        project_id: project?.project_id || (project as any)?.id || '',
        subcontractor_id: (subcontractor as any).id,
        type: 'POCR',
        protocol_date: payload.protocolDate,
        work_from_date: payload.workFromDate || undefined,
        work_to_date: payload.workToDate || undefined,
        contract_value: payload.contractValue,
        progress_percent: payload.progressPercent,
        value_since_start: payload.valueSinceStart,
        value_in_period: payload.valueInPeriod,
        remaining_to_invoice: payload.remainingToInvoice,
        gw_person1: payload.generalResponsible1,
        gw_person2: payload.generalResponsible2,
        subcontractor_person1: payload.subcontractorPerson1,
        subcontractor_person2: payload.subcontractorPerson2,
        defects_notes: payload.defectsNotes,
        summary_decision: payload.summaryDecision,
      }, user as any ? (user as any).google_access_token : undefined);
      showNotification('Protokół został utworzony', 'success');
    } catch (e) {
      console.error('Błąd tworzenia protokołu POCR:', e);
      showNotification('Nie udało się utworzyć protokołu', 'error');
    } finally {
      setSubmitting(false);
    }

    onSubmit(payload as any);
  };

  const sectionBox = { border: '1px solid #eee', borderRadius: 1, p: 2, backgroundColor: '#fafafa' } as const;
  const readonlyBoxSx = { '& .MuiOutlinedInput-root': { backgroundColor: '#f3f4f6' } } as const;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>PROTOKÓŁ ODBIORU CZĘŚCIOWEGO ROBÓT — {project?.project_id ? `POCR.${(project?.project_id || '').toString()}` : ''}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2"><b>Projekt:</b> {project?.project_id || '-'} — {project?.project_name || '-'}</Typography>
              <Typography variant="body2"><b>Podwykonawca:</b> {subcontractor.name} — <b>Umowa:</b> {subcontractor.contract_number}</Typography>
              <Typography variant="body2"><b>Przedmiot umowy:</b> {subcontractor.contract_subject}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Data protokołu" type="date" fullWidth value={protocolDate} onChange={(e) => setProtocolDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Roboty wykonano od" type="date" fullWidth value={workFromDate} onChange={(e) => setWorkFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Roboty wykonano do" type="date" fullWidth value={workToDate} onChange={(e) => setWorkToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <Box sx={{ ...sectionBox, height: '100%', display: 'flex', alignItems: 'center' }}>
              <TextField label="Wartość umowy" fullWidth value={formatPLN(contractValue)} InputProps={{ readOnly: true }} sx={{ ...readonlyBoxSx, '& .MuiInputBase-input': { fontWeight: 700 } }} />
            </Box>
          </Grid>
          <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
            <Box sx={{ ...sectionBox, height: '100%', width: '100%' }}>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Zaawansowanie umowy</Typography>
              <Slider value={progressPercent} onChange={handleProgressSlider} step={1} min={0} max={100} />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                <TextField label="Zaawansowanie (%)" type="number" value={progressPercent} onChange={handleProgressInput} inputProps={{ min: 0, max: 100, step: 0.1 }} size="small" sx={{ width: 200 }} />
                <Typography variant="body2" color="text.secondary">(możesz wpisać ręcznie lub użyć suwaka)</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={sectionBox}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField label="Wartość prac od początku" fullWidth value={formatPLN(valueSinceStart)} InputProps={{ readOnly: true }} sx={readonlyBoxSx} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Wartość prac w okresie" fullWidth type="number" value={valueInPeriod} onChange={handleValueInPeriodChange} InputProps={{ endAdornment: <InputAdornment position="end">PLN</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Pozostało do zafakturowania" fullWidth value={formatPLN(remainingToInvoice)} InputProps={{ readOnly: true }} sx={readonlyBoxSx} />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={sectionBox}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Przedstawiciele Generalnego Wykonawcy</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label="Osoba 1" fullWidth value={generalResponsible1} onChange={(e) => setGeneralResponsible1(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Osoba 2" fullWidth value={generalResponsible2} onChange={(e) => setGeneralResponsible2(e.target.value)} />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={sectionBox}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Przedstawiciele Podwykonawcy</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label="Osoba 1" fullWidth value={subPerson1} onChange={(e) => setSubPerson1(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Osoba 2" fullWidth value={subPerson2} onChange={(e) => setSubPerson2(e.target.value)} />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={sectionBox}>
              <TextField label="Wady/Usterki / Uwagi" fullWidth multiline rows={3} value={defects} onChange={(e) => setDefects(e.target.value)} />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={sectionBox}>
              <TextField 
                select 
                label="Podsumowanie" 
                fullWidth 
                value={summary} 
                onChange={(e) => setSummary(e.target.value as any)}
                SelectProps={{
                  MenuProps: {
                    disablePortal: true,
                    PaperProps: {
                      sx: {
                        maxWidth: 720,
                        width: 'calc(100% - 48px)',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        '& .MuiMenuItem-root': {
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.3,
                          fontSize: '0.9rem'
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="bez_uwag">Generalny wykonawca odbiera prace bez uwag. Zaawansowanie prac oraz rozliczenie finansowe zgodnie z zestawieniem ponizej.</MenuItem>
                <MenuItem value="z_uwagami">Generalny wykonawca odbiera prace z uwagami opisanymi w niniejszym protokole. Zaawansowanie prac oraz rozliczenie finansowe zgodnie z zestawieniem ponizej.</MenuItem>
                <MenuItem value="nie_odbiera_do_usuniecia">Generalny wykonawca nie odbiera prac - z uwagi na istotne usterki opisane w niniejszym protokole, które nadaja sie do usuniecia. Usterki zostaną usunięte w terminie opisanym w niniejszym protokole.</MenuItem>
                <MenuItem value="nie_odbiera_nie_do_usuniecia">Generalny wykonawca nie odbiera prac - z uwagi na istotne usterki opisane w niniejszym protokole, które nie nadają sie do usuniecia.</MenuItem>
              </TextField>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Anuluj</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Zapisywanie...' : 'Zapisz'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeneratePocrDialog;


